import { ImageAssetCard } from '@/components/Cards/ImageAssetCard';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Heart from '@/images/Heart.svg';
import HeartFilled from '@/images/HeartFilled.svg';
import InfoCircle from '@/images/InfoCircle.svg';
import { isNewPresenter } from '@/pages/agents/components/avatars/utils/IsNewPresenter';
import { isGreenScreenAvatar, isUserAvatar, isUserClipAvatar, useAddAvatarToFavourites } from '@/pages/avatar';
import theme from '@/theme';
import { isDvpPresenter, Presenter, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { ControlIconButton } from '../common/ControlIconButton';

const FavouriteButton = styled(ControlIconButton)<{ active?: boolean }>`
    display: none;
    width: 24px;
    height: 24px;
    border-radius: 4px !important;

    color: ${theme.colors.secondary[900]} !important;

    ${({ active }) =>
        active &&
        `
      color: ${theme.colors.primary[900]} !important;
    `}
`;

const DeprecationIndicator = styled.div`
    position: absolute;
    z-index: 1;
    top: 8px;
    right: 8px;
    display: flex;
    width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    background: rgba(9, 6, 4, 0.7);
    backdrop-filter: blur(8px);
`;

const StyledImageAssetCard = styled(ImageAssetCard)<{ selected: boolean }>`
    aspect-ratio: 16/9;
    background: transparent !important;
    padding: 0;
    overflow: hidden;

    border: 1px solid ${theme.colors.black[300]};

    ${({ selected, theme }) =>
        selected &&
        `
        border: 1px solid ${theme.colors.primary.main};
    `}

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    :hover ${FavouriteButton} {
        display: inline-flex;
    }

    .image-asset-card-image {
        border-radius: 0;
        background-size: contain;
    }
` as unknown as typeof ImageAssetCard;

interface Props {
    presenter: Presenter;
    onClick: (item: Presenter) => void;
    onRemove: (item: Presenter) => void;
    selected?: Presenter;
}

export const PresenterAssetCard = ({ presenter, onClick, onRemove, selected }: Props) => {
    const { isMutating, isLoading, isFavourite, toggle } = useAddAvatarToFavourites(presenter.id);

    const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        await toggle(e);
    };

    const showBlur =
        (isUserAvatar(presenter) || isUserClipAvatar(presenter)) && !isDvpPresenter(presenter) && !isGreenScreenAvatar(presenter);
    const isNew = useMemo(() => isNewPresenter(presenter), [presenter]);
    const canRemove = presenter.type !== VideoType.CLIP && !presenter.isDefault;

    return (
        <StyledImageAssetCard
            selected={presenter.id === selected?.id}
            key={presenter.order}
            testId={`presenter-asset-card-${presenter.id}`}
            item={presenter as Presenter}
            variant="lite-border"
            presenterId={presenter.id}
            type={presenter.type}
            src={presenter.thumbnail || presenter.poster}
            onRemove={canRemove ? async () => onRemove(presenter) : undefined}
            onClick={() => onClick(presenter)}
            isNew={isNew}
            showUpgradeBanner={false}
            disabled={false}
            isAiGenerated={presenter?.is_aiGenerated}
            showBlurredBackground={showBlur}
            sentiments={presenter.sentiments?.length}
            headerElements={
                <FavouriteButton ariaLabel="favourite" active={isFavourite} onClick={handleToggle} disabled={isLoading || isMutating}>
                    {isLoading ? (
                        <CircularProgress size={16} style={{ minWidth: '16px' }} />
                    ) : (
                        <MaskedIcon src={isFavourite ? HeartFilled : Heart} size="16px" />
                    )}
                </FavouriteButton>
            }>
            {presenter.deprecationDate && (
                <Tooltip
                    title={`This Avatar will be discontinued on ${format(parseISO(presenter.deprecationDate), 'MMMM do')}`}
                    slotProps={{
                        tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } },
                    }}
                    PopperProps={{
                        modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
                    }}
                    placement="top">
                    <DeprecationIndicator>
                        <MaskedIcon src={InfoCircle} size="16px" />
                    </DeprecationIndicator>
                </Tooltip>
            )}
        </StyledImageAssetCard>
    );
};
